import type { RailLayout } from "@/data/rail-branches";
import type { BookTimelineLayout } from "@/lib/timeline/book/BookLayout";
import { partStaySegments } from "../../rail/PartRunPhase/segments";
import { stayOverlapsBranch } from "../../rail/PartRunPhase/stayOverlap";
import {
  buildLocationRuns,
  locationRunsFromRail,
  type LocationRun,
} from "./runs";

const partRunsByRail = new WeakMap<RailLayout, LocationRun[]>();
const allRunsByLayout = new WeakMap<BookTimelineLayout, LocationRun[]>();

/** One box per visit to a part, so a later visit waits on the rail at its own slot. */
export function buildPartLocationRuns(rail: RailLayout): LocationRun[] {
  const cached = partRunsByRail.get(rail);
  if (cached !== undefined) {
    return cached;
  }
  const runs = computePartLocationRuns(rail);
  partRunsByRail.set(rail, runs);
  return runs;
}

function computePartLocationRuns(rail: RailLayout): LocationRun[] {
  const byKey = new Map<string, LocationRun>();
  for (const branch of rail.branches) {
    for (const stay of rail.stays) {
      if (!stayOverlapsBranch(stay, branch)) {
        continue;
      }
      const partLabel = stay.placeLabel;
      if (partLabel === undefined || partLabel === branch.label) {
        continue;
      }
      const placeId = stay.placeId ?? stay.rootPlaceId;
      const runId = `${branch.id}:${placeId}`;
      const startMs = Math.max(stay.startMs, branch.startMs);
      const endMs = Math.min(stay.endMs, branch.endMs);
      const existing = byKey.get(runId);
      if (existing === undefined) {
        byKey.set(runId, {
          runId,
          locationLabel: partLabel,
          startMs,
          endMs,
          lane: branch.lane,
        });
      } else {
        existing.startMs = Math.min(existing.startMs, startMs);
        existing.endMs = Math.max(existing.endMs, endMs);
      }
    }
  }
  return [...byKey.values()].flatMap((run) => partVisitRuns(rail, run));
}

function partVisitRuns(rail: RailLayout, run: LocationRun): LocationRun[] {
  const visits = partStaySegments(rail, run);
  if (visits.length <= 1) {
    return [run];
  }
  return visits.map((visit, index) => ({
    ...run,
    runId: index === 0 ? run.runId : `${run.runId}@${visit.startMs}`,
    startMs: visit.startMs,
    endMs: visit.endMs,
  }));
}

/** Scene sites, parallel branch sites, and part sites for mount and layout. */
export function allLocationRuns(layout: BookTimelineLayout): LocationRun[] {
  const cached = allRunsByLayout.get(layout);
  if (cached !== undefined) {
    return cached;
  }
  const runs = computeAllLocationRuns(layout);
  allRunsByLayout.set(layout, runs);
  return runs;
}

function computeAllLocationRuns(layout: BookTimelineLayout): LocationRun[] {
  const byId = new Map<string, LocationRun>();
  for (const run of buildLocationRuns(layout.beats)) {
    byId.set(run.runId, run);
  }
  if (layout.rail.branches.length > 0) {
    for (const run of locationRunsFromRail(layout.rail.branches, layout.beats)) {
      // Main-line sites use scene runs; keep rail rows for parallel lanes only.
      if ((run.lane ?? 0) !== 0) {
        byId.set(run.runId, run);
      }
    }
    for (const run of buildPartLocationRuns(layout.rail)) {
      byId.set(run.runId, run);
    }
    dropSceneSitesOnForkBranches(layout, byId);
  }
  return [...byId.values()].sort((a, b) => a.startMs - b.startMs);
}

/** Fork-line site boxes use the rail branch run, not duplicate scene runs. */
function dropSceneSitesOnForkBranches(
  layout: BookTimelineLayout,
  byId: Map<string, LocationRun>,
): void {
  for (const branch of layout.rail.branches) {
    if ((branch.lane ?? 0) === 0) {
      continue;
    }
    for (const run of buildLocationRuns(layout.beats)) {
      if (run.locationLabel !== branch.label) {
        continue;
      }
      if (run.startMs >= branch.startMs && run.startMs < branch.endMs) {
        byId.delete(run.runId);
      }
    }
  }
}
